import type { EnglishMvpSnapshot } from './englishMvp';

export type ReportTier = 'timing' | 'snapshot' | 'full';
export type ReportGoal = 'job_change' | 'business_growth' | 'income_stability' | 'workload_balance' | 'annual_planning';

export type ReportEvidence = {
  kind: 'palace' | 'star' | 'transformation' | 'cycle' | 'annual';
  label: string;
};

export type PaidReportSection = {
  id: string;
  kicker: string;
  title: string;
  traditional: string;
  practical: string;
  evidence: ReportEvidence[];
};

export type PaidReport = {
  version: 'ziheng-report-v2';
  tier: ReportTier;
  goal: ReportGoal;
  title: string;
  subtitle: string;
  year: number;
  chartDate: string;
  dateBasis?: { recordedDate: string; effectiveTime: string; lunarDate: string; annualReferenceDate: string };
  executiveSummary: string;
  chartPortrait: {
    title: string;
    subtitle: string;
    narrative: string;
    nodes: Array<{
      role: 'CORE' | 'WORK' | 'RESOURCE' | 'HORIZON';
      palace: string;
      stars: string[];
      transformation?: string;
    }>;
    traits: Array<{ label: string; description: string }>;
  };
  decisionCompass: {
    leverage: string[];
    guardrails: string[];
    proof: string[];
    questions: string[];
  };
  scenarios: Array<{
    title: string;
    situation: string;
    chartReading: string;
    move: string;
    evidence: string;
  }>;
  preview: {
    headline: string;
    insight: string;
    lockedQuestions: string[];
    evidence: ReportEvidence[];
  };
  sections: PaidReportSection[];
  priorityWindows: Array<{
    month: string;
    palace: string;
    mode: 'ACT' | 'PREPARE' | 'REVIEW';
    interpretation: string;
    action: string;
    bestFor: string;
    watchFor: string;
    checkpoint: string;
    evidence: ReportEvidence[];
  }>;
  monthlyMap: Array<{
    month: string;
    palace: string;
    mode: 'BUILD' | 'CONNECT' | 'REVIEW' | 'RECOVER';
    interpretation: string;
    action: string;
    watch: string;
    proof: string;
  }>;
  actionPlan: Array<{ horizon: string; action: string; measure: string }>;
  methodology: string[];
  disclaimer: string;
};

export const REPORT_GOALS: Array<{ value: ReportGoal; label: string; description: string }> = [
  { value: 'annual_planning', label: 'Plan the year', description: 'Sequence action, preparation, review, and recovery across the selected year.' },
  { value: 'job_change', label: 'Change roles', description: 'Test a role or career move without forcing the timing.' },
  { value: 'business_growth', label: 'Grow a business', description: 'Clarify offer, audience, visibility, and resource use.' },
  { value: 'income_stability', label: 'Stabilise income', description: 'Strengthen the return loop, buffers, and repeatable value.' },
  { value: 'workload_balance', label: 'Improve workload', description: 'Protect capacity while keeping useful work moving.' },
];

const STAR_THEMES: Record<string, { gift: string; watch: string; move: string }> = {
  Emperor: { gift: 'setting direction and holding a coherent standard', watch: 'waiting for perfect authority before acting', move: 'name the standard, then make one decision that demonstrates it' },
  Advisor: { gift: 'seeing alternatives and adapting a plan', watch: 'turning every possibility into another delay', move: 'limit the experiment, choose a review date, and learn quickly' },
  Sun: { gift: 'making a contribution visible and useful to others', watch: 'confusing constant visibility with sustainable value', move: 'publish one useful signal and protect the capacity behind it' },
  General: { gift: 'organising resources, targets, and execution', watch: 'measuring only what is easy to count', move: 'connect the metric to a real customer, result, or reserve' },
  Fortunate: { gift: 'creating ease, cooperation, and humane momentum', watch: 'staying comfortable after the situation has changed', move: 'keep what works, but add one deliberate stretch' },
  Judge: { gift: 'testing standards and noticing what needs correction', watch: 'letting scrutiny become rigidity or self-criticism', move: 'turn one criticism into a concrete quality rule' },
  Empress: { gift: 'cultivating assets, systems, and steady support', watch: 'over-protecting a resource that now needs circulation', move: 'choose what to preserve and what to put back into use' },
  Moon: { gift: 'reading subtle patterns and building value quietly', watch: 'keeping useful insight invisible for too long', move: 'document the pattern and share it with one trusted audience' },
  Wolf: { gift: 'opening new markets, relationships, and experiments', watch: 'chasing novelty before a return loop is clear', move: 'test one new channel with a fixed budget and success measure' },
  Advocator: { gift: 'asking difficult questions and clarifying a position', watch: 'letting debate replace a decision', move: 'write the claim, the evidence, and the next decision separately' },
  Minister: { gift: 'coordinating people, expectations, and delivery', watch: 'absorbing responsibility that was never made explicit', move: 'clarify ownership, hand-offs, and the definition of done' },
  Sage: { gift: 'protecting perspective and supporting durable judgement', watch: 'using wisdom as distance from practical detail', move: 'translate the principle into one repeatable operating rule' },
  Marshal: { gift: 'making a decisive move under pressure', watch: 'treating every situation as an emergency', move: 'separate the irreversible decision from the small test' },
  Rebel: { gift: 'rebuilding a system that no longer fits', watch: 'destroying useful foundations along with the constraint', move: 'keep one stable base while redesigning one layer at a time' },
};

const STAR_ARCHETYPES: Record<string, string> = {
  Emperor: 'Standards Setter', Advisor: 'Adaptive Strategist', Sun: 'Visible Contributor', General: 'Resource Commander',
  Fortunate: 'Flow Builder', Judge: 'Quality Architect', Empress: 'Asset Curator', Moon: 'Pattern Reader',
  Wolf: 'Market Explorer', Advocator: 'Position Clarifier', Minister: 'Delivery Integrator', Sage: 'Long-Horizon Guide',
  Marshal: 'Decisive Operator', Rebel: 'System Rebuilder',
};

const PALACE_ACTIONS: Record<string, { lens: string; action: string }> = {
  'Life & Direction': { lens: 'identity, standards, and the direction choices reinforce', action: 'write the role or body of work you want the next decision to strengthen' },
  'Roots & Guidance': { lens: 'context, inherited assumptions, and dependable support', action: 'ask for one piece of context or experienced feedback before deciding alone' },
  'Well-being & Inner Drive': { lens: 'attention, recovery, and internal operating capacity', action: 'remove one avoidable drain and protect a repeatable recovery block' },
  'Home & Foundations': { lens: 'routines, environment, and material foundations', action: 'stabilise one base system: calendar, workspace, cash buffer, or household agreement' },
  'Career & Public Path': { lens: 'contribution, responsibility, and visible professional direction', action: 'make one capability visible through a finished, reviewable result' },
  'Network & Collaboration': { lens: 'alliances, reciprocity, and shared ownership', action: 'make one specific ask and state what you can contribute in return' },
  'Travel & Outer World': { lens: 'new audiences, markets, clients, and unfamiliar environments', action: 'run a bounded test in a wider field before making a large commitment' },
  'Energy & Recovery': { lens: 'pace, workload, and sustainable capacity', action: 'treat recovery as a planning input and seek qualified care for health concerns' },
  'Wealth & Resources': { lens: 'earning patterns, allocation, pricing, and buffers', action: 'trace one return loop from effort to value, price, payment, and retained resource' },
  'Creativity & Legacy': { lens: 'ideas, output, teaching, and what continues after delivery', action: 'ship a small repeatable version and collect evidence from its use' },
  'Partnership & Commitment': { lens: 'expectations, responsibilities, and durable agreements', action: 'make one expectation explicit before treating it as a commitment' },
  'Peers & Collaboration': { lens: 'comparison, equality, and peer-level coordination', action: 'clarify ownership and use peers as evidence, not as a ranking system' },
};

const GOAL_COPY: Record<ReportGoal, { title: string; question: string; actions: [string, string, string] }> = {
  annual_planning: {
    title: 'Annual Planning',
    question: 'Which windows are better for action, preparation, review, or recovery?',
    actions: ['Name the one result this year must produce.', 'Match major commitments to the monthly evidence map.', 'Set quarterly checkpoints before the year becomes reactive.'],
  },
  job_change: {
    title: 'Role-change planning lens',
    question: 'What evidence should exist before a role change becomes a decision rather than an escape?',
    actions: ['Define the work conditions that must change.', 'Test the target role through one conversation or project.', 'Choose a decision date and the evidence required by then.'],
  },
  business_growth: {
    title: 'Business-growth planning lens',
    question: 'Which contribution can become a clearer offer, and which resource must support repeatable delivery?',
    actions: ['Name one audience and one costly problem.', 'Package one visible result into a small offer.', 'Review margin, delivery effort, and repeat demand together.'],
  },
  income_stability: {
    title: 'Income-stability planning lens',
    question: 'Where does useful effort fail to become a repeatable and protected return loop?',
    actions: ['Separate recurring, variable, and speculative income.', 'Strengthen one repeatable source before adding another.', 'Set a buffer rule and review it monthly.'],
  },
  workload_balance: {
    title: 'Sustainable-work planning lens',
    question: 'Which work deserves energy, and which commitment is consuming capacity without enough return?',
    actions: ['Measure load by energy as well as hours.', 'Remove, delegate, or narrow one low-return commitment.', 'Protect a weekly block for recovery and strategic work.'],
  },
};

const GOAL_REPORT_LENS: Record<ReportGoal, {
  palacePriority: string[];
  career: string;
  wealth: string;
  horizon: string;
  proof: [string, string, string];
  question: string;
}> = {
  annual_planning: {
    palacePriority: ['Life & Direction', 'Career & Public Path', 'Well-being & Inner Drive', 'Wealth & Resources'],
    career: 'Sequence visible work around capacity instead of treating every month as an action month.',
    wealth: 'Reserve time and money before expansion so the annual plan remains usable under pressure.',
    horizon: 'Use the outer-world palace to decide when a wider test needs preparation rather than immediate scale.',
    proof: ['One annual result with a dated owner.', 'A quarterly resource and capacity review.', 'A written stop, continue, or revise decision.'],
    question: 'What must be true by the next review date for the year to remain on course?',
  },
  job_change: {
    palacePriority: ['Career & Public Path', 'Travel & Outer World', 'Life & Direction', 'Network & Collaboration'],
    career: 'Translate the Career Palace into a role scorecard: scope, authority, standards, and the result you will own.',
    wealth: 'Compare salary with runway, workload, learning value, and the cost of losing current options.',
    horizon: 'Use the outer-world pattern for interviews, market tests, and conversations before an irreversible exit.',
    proof: ['A written role scorecard.', 'One direct test of the target work.', 'A runway and reversibility check.'],
    question: 'Is the next role measurably better, or only emotionally different from the current one?',
  },
  business_growth: {
    palacePriority: ['Career & Public Path', 'Wealth & Resources', 'Travel & Outer World', 'Network & Collaboration'],
    career: 'Turn the Career Palace contribution into one named offer with an owner and definition of done.',
    wealth: 'Read the Wealth Palace through price, margin, collection, delivery effort, and retained cash—not attention alone.',
    horizon: 'Use the outer-world pattern to test one audience or channel with a fixed budget and review date.',
    proof: ['Three comparable deliveries.', 'Margin and capacity recorded together.', 'A channel test with a stop condition.'],
    question: 'Which part of demand can become repeatable without making delivery fragile?',
  },
  income_stability: {
    palacePriority: ['Wealth & Resources', 'Career & Public Path', 'Home & Foundations', 'Well-being & Inner Drive'],
    career: 'Identify which contribution produces repeat demand instead of adding another unrelated income stream.',
    wealth: 'Separate booked revenue, collected cash, recurring income, variable income, and speculative upside.',
    horizon: 'Treat wider opportunities as optional tests until the core return loop and buffer are protected.',
    proof: ['A source-by-source income map.', 'A monthly collection and buffer rule.', 'One repeated offer or responsibility.'],
    question: 'Which income source remains useful after volatility, time cost, and collection risk are included?',
  },
  workload_balance: {
    palacePriority: ['Energy & Recovery', 'Well-being & Inner Drive', 'Career & Public Path', 'Home & Foundations'],
    career: 'Define the service level each responsibility actually requires instead of applying maximum effort everywhere.',
    wealth: 'Include coordination, recovery, and rework in the real cost of each commitment.',
    horizon: 'Delay wider expansion until the current delivery system can repeat without emergency effort.',
    proof: ['A weekly energy and load record.', 'One delegated, narrowed, or declined commitment.', 'A repeatable shutdown and recovery block.'],
    question: 'Which commitment consumes capacity without producing enough value, learning, or trust?',
  },
};

const GOAL_SCENARIOS: Record<ReportGoal, Array<{ title: string; situation: string; proof: string }>> = {
  annual_planning: [
    { title: 'When the year feels crowded', situation: 'Several priorities compete for the same time, money, or attention.', proof: 'A useful plan separates action windows from preparation, review, and recovery.' },
    { title: 'Before a dated commitment', situation: 'A launch, move, application, or negotiation needs a realistic sequence.', proof: 'The decision becomes stronger when the calendar, resources, and stop conditions agree.' },
  ],
  job_change: [
    { title: 'The attractive but vague role', situation: 'A new title looks promising, but ownership, quality standards, and the actual exchange are unclear.', proof: 'A written role scorecard and one conversation with the future decision-maker.' },
    { title: 'The current role feels too narrow', situation: 'The work still functions, yet it no longer gives enough room for the contribution this chart wants to make visible.', proof: 'One adjacent project that tests the missing scope without requiring an immediate exit.' },
    { title: 'An offer arrives before readiness', situation: 'Timing creates urgency before money, workload, or support systems have been checked.', proof: 'A decision memo covering runway, reversibility, workload, and the first 90 days.' },
  ],
  business_growth: [
    { title: 'Interest without repeat demand', situation: 'People respond to the idea, but the offer is still broad and delivery changes every time.', proof: 'One defined audience, one costly problem, and three comparable deliveries.' },
    { title: 'Growth increases hidden workload', situation: 'Revenue or attention rises while coordination and recovery costs remain invisible.', proof: 'A delivery map showing owner, hand-off, time cost, margin, and capacity limit.' },
    { title: 'A new channel looks exciting', situation: 'A wider market is available, but novelty can outrun the return loop.', proof: 'A capped channel test with a budget, success measure, and stop date.' },
  ],
  income_stability: [
    { title: 'Useful work, irregular return', situation: 'The contribution is real, but pricing, collection, or repeatability is inconsistent.', proof: 'A traced path from effort to value, price, payment date, and retained cash.' },
    { title: 'Too many small income streams', situation: 'Optionality feels safe while attention fragments and no source becomes dependable.', proof: 'A 90-day comparison of repeat demand, margin, volatility, and energy cost.' },
    { title: 'The buffer keeps being postponed', situation: 'Current delivery absorbs every resource, leaving little room for choice.', proof: 'A fixed buffer rule applied automatically before discretionary expansion.' },
  ],
  workload_balance: [
    { title: 'Everything looks equally urgent', situation: 'Quality standards and responsibility combine until every task receives the same intensity.', proof: 'A visible service level: critical, sufficient, delegated, or declined.' },
    { title: 'Invisible coordination dominates', situation: 'Meetings, checking, and follow-up consume capacity without appearing as output.', proof: 'A weekly load review separating creation, coordination, recovery, and rework.' },
    { title: 'Rest becomes another task', situation: 'Recovery is scheduled but remains mentally attached to unfinished work.', proof: 'One protected recovery block plus a written shutdown and restart ritual.' },
  ],
};

function palace(snapshot: EnglishMvpSnapshot, key: string) {
  return snapshot.palaces.find((item) => item.key === key) ?? snapshot.palaces[0]!;
}

function starTheme(stars: string[]) {
  const name = stars[0] || 'Whole chart';
  return { name, ...(STAR_THEMES[name] ?? { gift: 'connecting several parts of the chart before deciding', watch: 'reducing a complex situation to one label', move: 'compare the chart prompt with observable evidence' }) };
}

function evidenceForPalace(item: EnglishMvpSnapshot['palaces'][number]): ReportEvidence[] {
  return [
    { kind: 'palace', label: item.label },
    ...item.stars.slice(0, 2).map((star) => ({ kind: 'star' as const, label: star })),
    ...item.transformations.map((transformation) => ({
      kind: 'transformation' as const,
      label: `${transformation.star} · ${transformation.traditionalName} ${transformation.label}`,
    })),
  ];
}

function transformationSentence(item: EnglishMvpSnapshot['palaces'][number]) {
  if (!item.transformations.length) return 'No natal Four Transformation is used here as a shortcut; the palace and its connected domains remain the basis.';
  return item.transformations.map((transformation) => `${transformation.star} carries ${transformation.traditionalName} (${transformation.label.toLowerCase()}) in this palace`).join('; ') + '.';
}

function buildPalaceSection(id: string, kicker: string, item: EnglishMvpSnapshot['palaces'][number], practicalPrefix = ''): PaidReportSection {
  const theme = starTheme(item.stars);
  const palaceAction = PALACE_ACTIONS[item.label] ?? PALACE_ACTIONS['Life & Direction'];
  return {
    id,
    kicker,
    title: `${item.label}: ${theme.name} at work`,
    traditional: `${item.label} is read as a domain of ${palaceAction.lens}. ${theme.name} adds a pattern of ${theme.gift}, with a watch-point around ${theme.watch}. ${transformationSentence(item)}`,
    practical: `${practicalPrefix}${theme.move}; then ${palaceAction.action}. Treat the result as evidence to review, not as a promised outcome.`,
    evidence: evidenceForPalace(item),
  };
}

function monthlyMode(palaceLabel: string): PaidReport['monthlyMap'][number]['mode'] {
  if (['Career & Public Path', 'Creativity & Legacy', 'Life & Direction'].includes(palaceLabel)) return 'BUILD';
  if (['Network & Collaboration', 'Travel & Outer World', 'Partnership & Commitment', 'Peers & Collaboration'].includes(palaceLabel)) return 'CONNECT';
  if (['Well-being & Inner Drive', 'Energy & Recovery', 'Home & Foundations'].includes(palaceLabel)) return 'RECOVER';
  return 'REVIEW';
}

function priorityMode(index: number): 'ACT' | 'PREPARE' | 'REVIEW' {
  return index === 0 ? 'ACT' : index === 1 ? 'PREPARE' : 'REVIEW';
}

function proofForMode(mode: PaidReport['monthlyMap'][number]['mode']) {
  if (mode === 'BUILD') return 'A finished artifact, decision, or repeatable operating rule.';
  if (mode === 'CONNECT') return 'A reciprocal agreement, useful response, or clearly owned hand-off.';
  if (mode === 'RECOVER') return 'Capacity returns without creating a hidden backlog or broken promise.';
  return 'One number, boundary, assumption, or allocation becomes clearer.';
}

export function buildPaidReport(snapshot: EnglishMvpSnapshot, goal: ReportGoal, tier: ReportTier): PaidReport {
  const career = palace(snapshot, 'career');
  const wealth = palace(snapshot, 'wealth');
  const life = palace(snapshot, 'soul');
  const travel = palace(snapshot, 'surface');
  const travelTheme = starTheme(travel.stars);
  const lifeTheme = starTheme(life.stars);
  const careerTheme = starTheme(career.stars);
  const wealthTheme = starTheme(wealth.stars);
  const goalCopy = GOAL_COPY[goal];
  const goalLens = GOAL_REPORT_LENS[goal];
  const currentAction = PALACE_ACTIONS[snapshot.currentCycle.palace] ?? PALACE_ACTIONS['Life & Direction'];
  const nextAction = PALACE_ACTIONS[snapshot.nextCycle.palace] ?? PALACE_ACTIONS['Life & Direction'];
  const rankedWindows = snapshot.annual.monthlyWindows.map((month, monthIndex) => {
    const sourcePalace = snapshot.palaces.find((item) => item.label === month.palace) ?? life;
    const goalRank = goalLens.palacePriority.indexOf(month.palace);
    const reasons: string[] = [];
    let score = sourcePalace.stars.length ? 1 : 0;
    if (month.palace === snapshot.annual.palace) { score += 6; reasons.push('the annual palace repeats here'); }
    if (month.palace === snapshot.currentCycle.palace) { score += 4; reasons.push('the active decade domain repeats here'); }
    if (goalRank >= 0) { score += Math.max(1, 4 - goalRank); reasons.push(`it is directly relevant to ${goalCopy.title.toLowerCase()}`); }
    if (sourcePalace.transformations.length) {
      score += sourcePalace.transformations.length * 2;
      reasons.push(`it carries ${sourcePalace.transformations.map((item) => `${item.traditionalName} ${item.label}`).join(' and ')}`);
    }
    if (!reasons.length) reasons.push('its palace theme supplies a useful review lens for the selected goal');
    return { month, monthIndex, sourcePalace, score, reasons };
  }).sort((a, b) => b.score - a.score || a.monthIndex - b.monthIndex);
  const priorityIndices = rankedWindows.slice(0, 3).map((item) => item.monthIndex);
  const priorityWindows = priorityIndices.map((monthIndex, index) => {
    const month = snapshot.annual.monthlyWindows[monthIndex]!;
    const sourcePalace = snapshot.palaces.find((item) => item.label === month.palace) ?? life;
    const action = PALACE_ACTIONS[month.palace] ?? PALACE_ACTIONS['Life & Direction'];
    const ranked = rankedWindows.find((item) => item.monthIndex === monthIndex)!;
    return {
      month: month.month,
      palace: month.palace,
      mode: priorityMode(index),
      interpretation: `${month.palace} foregrounds ${action.lens}. This window ranks because ${ranked.reasons.join('; ')}. It is a planning checkpoint, not a promised event.`,
      action: index === 0 ? goalCopy.actions[0] : index === 1 ? goalCopy.actions[1] : goalCopy.actions[2],
      bestFor: action.action,
      watchFor: starTheme(sourcePalace.stars).watch,
      checkpoint: index === 0 ? 'A visible first move exists.' : index === 1 ? 'The supporting condition is documented.' : 'Evidence and cost are reviewed before recommitting.',
      evidence: [
        { kind: 'annual' as const, label: `${snapshot.annual.year} · ${month.month}` },
        ...(month.palace === snapshot.currentCycle.palace ? [{ kind: 'cycle' as const, label: `${snapshot.currentCycle.range} · ${snapshot.currentCycle.palace}` }] : []),
        ...evidenceForPalace(sourcePalace).slice(0, 4),
      ],
    };
  });
  const monthlyMap = snapshot.annual.monthlyWindows.map((month) => {
    const sourcePalace = snapshot.palaces.find((item) => item.label === month.palace) ?? life;
    const action = PALACE_ACTIONS[month.palace] ?? PALACE_ACTIONS['Life & Direction'];
    const mode = monthlyMode(month.palace);
    return {
      ...month,
      mode,
      interpretation: `${month.palace} brings ${action.lens} into the monthly review.`,
      action: action.action,
      watch: starTheme(sourcePalace.stars).watch,
      proof: proofForMode(mode),
    };
  });

  const archetype = STAR_ARCHETYPES[careerTheme.name] ?? 'Whole-Chart Builder';
  const cycleShort = snapshot.currentCycle.palace.replace(' & ', ' / ');
  const chartPortrait: PaidReport['chartPortrait'] = {
    title: `The ${archetype} in a ${cycleShort} cycle`,
    subtitle: `${careerTheme.name} shapes visible contribution; ${wealthTheme.name} describes how value is organised and retained.`,
    narrative: `This chart language suggests a person who does best when ${careerTheme.gift} is supported by ${wealthTheme.gift}. ${lifeTheme.name} adds ${lifeTheme.gift}, while ${travelTheme.name} shows how the pattern meets new audiences and unfamiliar environments. The active ${snapshot.currentCycle.range} cycle makes ${snapshot.currentCycle.palace.toLowerCase()} the immediate context, so progress is less about forcing a single outcome and more about making expectations, evidence, and exchange explicit.`,
    nodes: [
      { role: 'CORE', palace: life.label, stars: life.stars.slice(0, 2), transformation: life.transformations[0] ? `${life.transformations[0].traditionalName} ${life.transformations[0].label}` : undefined },
      { role: 'WORK', palace: career.label, stars: career.stars.slice(0, 2), transformation: career.transformations[0] ? `${career.transformations[0].traditionalName} ${career.transformations[0].label}` : undefined },
      { role: 'RESOURCE', palace: wealth.label, stars: wealth.stars.slice(0, 2), transformation: wealth.transformations[0] ? `${wealth.transformations[0].traditionalName} ${wealth.transformations[0].label}` : undefined },
      { role: 'HORIZON', palace: travel.label, stars: travel.stars.slice(0, 2), transformation: travel.transformations[0] ? `${travel.transformations[0].traditionalName} ${travel.transformations[0].label}` : undefined },
    ],
    traits: [
      { label: 'When this pattern is working well', description: `You turn ${careerTheme.gift} into something other people can recognise, use, or trust.` },
      { label: 'How value becomes sustainable', description: `The Wealth Palace asks for ${wealthTheme.gift}; usefulness needs a price, owner, buffer, and repeatable delivery path.` },
      { label: 'What pressure can distort', description: `${careerTheme.watch} can combine with ${wealthTheme.watch}. This is a guardrail, not a fixed personality label.` },
      { label: 'The fastest useful reset', description: `${careerTheme.move}; then ${wealthTheme.move}. Keep the next move observable and reversible where possible.` },
    ],
  };

  const decisionCompass: PaidReport['decisionCompass'] = {
    leverage: [careerTheme.move, wealthTheme.move, travelTheme.move],
    guardrails: [careerTheme.watch, wealthTheme.watch, travelTheme.watch],
    proof: goalLens.proof,
    questions: [
      goalLens.question,
      'What evidence would change my mind, and when will I review it?',
      'Who owns the next step, and what resource must be protected first?',
    ],
  };

  const scenarios: PaidReport['scenarios'] = GOAL_SCENARIOS[goal].map((scenario, index) => ({
    title: scenario.title,
    situation: scenario.situation,
    chartReading: index === 0
      ? `${careerTheme.name} works best through ${careerTheme.gift}; the current ${snapshot.currentCycle.palace} cycle asks for clearer expectations before commitment.`
      : index === 1
        ? `${wealthTheme.name} makes ${wealthTheme.gift} part of the decision, so the return loop and hidden resource cost matter.`
        : `${travelTheme.name} can support ${travelTheme.gift}, provided the experiment has a boundary and review point.`,
    move: goalCopy.actions[index]!,
    evidence: scenario.proof,
  }));

  const sections: PaidReportSection[] = [
    {
      id: 'current-cycle',
      kicker: `CURRENT DECADE · ${snapshot.currentCycle.range}`,
      title: `${snapshot.currentCycle.palace} is the active planning context`,
      traditional: `The current 10-year layer places ${snapshot.currentCycle.palace.toLowerCase()} in the foreground. It does not replace the natal chart; it changes which domain is most useful to question now.`,
      practical: `Use the cycle to ${currentAction.action}. For ${goalCopy.title.toLowerCase()}, ask: ${goalCopy.question}`,
      evidence: [{ kind: 'cycle', label: `${snapshot.currentCycle.range} · ${snapshot.currentCycle.palace}` }],
    },
    buildPalaceSection('career', 'CAREER PALACE', career, `${goalLens.career} `),
    buildPalaceSection('wealth', 'WEALTH PALACE', wealth, `${goalLens.wealth} `),
    {
      id: 'career-wealth-synthesis',
      kicker: 'CAREER × WEALTH',
      title: `${careerTheme.name} contribution × ${wealthTheme.name} resource pattern`,
      traditional: `Career describes how contribution and responsibility become visible; Wealth describes how value is allocated, priced, protected, and sustained. In this chart, ${careerTheme.gift} needs to work with ${wealthTheme.gift}.`,
      practical: `${goalLens.career} ${goalLens.wealth} Build one observable return loop: contribution → evidence of value → clear exchange → retained resource. Watch for ${careerTheme.watch} and ${wealthTheme.watch}.`,
      evidence: [...evidenceForPalace(career), ...evidenceForPalace(wealth)],
    },
  ];

  if (tier === 'full') {
    sections.splice(1, 0, {
      id: 'next-cycle',
      kicker: `NEXT DECADE · ${snapshot.nextCycle.range}`,
      title: `Prepare for ${snapshot.nextCycle.palace} without living there early`,
      traditional: `The next visible decade layer moves attention toward ${snapshot.nextCycle.palace.toLowerCase()}. This is a preparation horizon, not a reason to force an event before its context exists.`,
      practical: `Keep one foundation that will make the shift easier: ${nextAction.action}. Review readiness through evidence, options, and capacity.`,
      evidence: [{ kind: 'cycle', label: `${snapshot.nextCycle.range} · ${snapshot.nextCycle.palace}` }],
    });
    sections.push(
      buildPalaceSection('life-career', 'LIFE × CAREER', life, 'Connect identity with contribution: '),
      buildPalaceSection('wealth-travel', 'WEALTH × OUTER WORLD', travel, `${goalLens.horizon} Test the resource loop in the wider field: `),
      {
        id: 'goal',
        kicker: 'YOUR SELECTED QUESTION',
        title: goalCopy.title,
        traditional: `A chart does not choose for you. It can identify which domains and timing layers deserve a clearer question. Your selected lens is: ${goalCopy.question}`,
        practical: goalCopy.actions.join(' '),
        evidence: [
          { kind: 'palace', label: career.label },
          { kind: 'palace', label: wealth.label },
          { kind: 'cycle', label: snapshot.currentCycle.range },
          { kind: 'annual', label: String(snapshot.annual.year) },
        ],
      },
    );
  }

  // The entry-price timing product is the twelve-month map and the cycle it
  // sits in — deliberately without the palace read-throughs that make the
  // Snapshot and the Full Report worth their price.
  const deliveredSections = tier === 'timing' ? sections.slice(0, 1) : sections;

  const headline = `${career.stars[0] || 'Your whole chart'} shapes Career while ${snapshot.currentCycle.palace} leads the current cycle.`;
  return {
    version: 'ziheng-report-v2',
    tier,
    goal,
    title: tier === 'timing' ? 'Annual Timing Map' : tier === 'snapshot' ? 'Career & Money Snapshot' : 'Career & Wealth Timing Report',
    subtitle: `${snapshot.annual.year} · ${goalCopy.title}`,
    year: snapshot.annual.year,
    chartDate: snapshot.effectiveBirthDate,
    dateBasis: { recordedDate: snapshot.birthDate, effectiveTime: snapshot.effectiveBirthTime, lunarDate: snapshot.lunarDate, annualReferenceDate: `${snapshot.annual.year}-07-01` },
    executiveSummary: `${headline} The useful task is to connect ${careerTheme.gift} with ${wealthTheme.gift}, then test the pattern through the ${snapshot.annual.year} planning layer.`,
    chartPortrait,
    decisionCompass,
    scenarios,
    preview: {
      headline,
      insight: `Your Career Palace emphasizes ${careerTheme.gift}. Your Wealth Palace asks that this becomes sustainable through ${wealthTheme.gift}. The current decade adds ${snapshot.currentCycle.palace.toLowerCase()} as the immediate context.`,
      lockedQuestions: [
        `Which ${snapshot.annual.year} windows are better used to act, prepare, or review?`,
        `What guardrail helps ${careerTheme.name} avoid this watch-point: ${careerTheme.watch}?`,
        `What should be built before the ${snapshot.nextCycle.range} shift toward ${snapshot.nextCycle.palace}?`,
      ],
      evidence: [...evidenceForPalace(career), ...evidenceForPalace(wealth), { kind: 'cycle', label: `${snapshot.currentCycle.range} · ${snapshot.currentCycle.palace}` }],
    },
    sections: deliveredSections,
    priorityWindows,
    monthlyMap,
    actionPlan: [
      { horizon: 'This week', action: goalCopy.actions[0], measure: 'One completed artifact, conversation, or decision note.' },
      { horizon: 'This month', action: goalCopy.actions[1], measure: 'Track energy, response, output, and money separately.' },
      { horizon: `Before ${snapshot.nextCycle.range}`, action: goalCopy.actions[2], measure: 'A written readiness checklist grounded in real constraints.' },
    ],
    methodology: [
      'Natal Life, Career, Wealth, and Travel palaces are read as a connected system.',
      `The active ${snapshot.currentCycle.range} and next ${snapshot.nextCycle.range} decade layers provide planning context.`,
      `${snapshot.annual.year} annual and monthly placements organize review windows; they do not guarantee events.`,
      'Four Transformations are shown as evidence only when they are present in the calculated natal placement.',
      'Birth details remain in the browser while this report is generated.',
    ],
    disclaimer: 'Ziheng is a cultural education and personal-reflection tool. It does not guarantee income, promotion, investment returns, relationships, health outcomes, or any event, and it does not replace financial, legal, medical, or mental-health advice.',
  };
}
